import React from "react";
import formatTimestamp from "../../ReusableFunctions/formatTimestamp";
import RagConceptBanner from "./RagConceptBanner";
import RagStudyCard from "./RagStudyCard";
import RagCollections from "./RagCollections";
import RagYearBanner from "./RagYearBanner";
import RagDateBanner from "./RagDateBanner";
import SectionChip from "./SectionChip";

const FACET_ICON = { glossary: "📖" };

export default function RagResults({ results, requestId, onMomentClick, onConceptSearch }) {
  const r = results || {};
  const facets = r.facets || [];
  const collections = r.collections || [];
  const hasContent =
    r.resolved_concept ||
    r.study ||
    r.year ||
    r.date ||
    collections.length > 0 ||
    facets.length > 0;

  if (!hasContent) {
    return (
      <div className="flex justify-center py-10 text-primaryDark">
        No results — try a concept, class name, or year
      </div>
    );
  }

  let rank = 0;
  return (
    <div>
      <RagYearBanner year={r.year} />
      <RagDateBanner date={r.date} />
      <RagConceptBanner concept={r.resolved_concept} onConceptSearch={onConceptSearch} />
      <RagStudyCard study={r.study} />
      <RagCollections collections={collections} requestId={requestId} />
      <div className="space-y-8">
        {facets.map((facet) => (
          <section key={facet.facet}>
            <h2 className="text-[22px] md:text-2xl font-extrabold text-primaryDark mb-4">
              {FACET_ICON[facet.facet] ? (
                <span aria-hidden="true">{`${FACET_ICON[facet.facet]} `}</span>
              ) : null}
              <span>{facet.label}</span>
            </h2>
            <div className="space-y-4">
              {(facet.groups || []).map((group) => {
                rank += 1;
                const groupRank = rank;
                const items = group.items || [];
                const first = items[0];
                // The class-profile summary ("This class discusses …") replaces raw
                // transcript snippets; unprofiled classes fall back to the first snippet.
                const description = group.summary || (first && first.snippet);
                // 0:00 = "the class from the start" — the title click covers that; only
                // mid-class moments earn a timestamp chip.
                const moments = items.filter((item) => (item.start_ms || 0) > 0);
                return (
                  <div
                    key={group.class_id}
                    className="rounded-lg bg-[#D2D6DC] bg-opacity-[0.4] p-4"
                  >
                    <div
                      className="flex items-center gap-2 cursor-pointer hover:text-primaryBlue"
                      onClick={() => first && onMomentClick(group, first, groupRank)}
                    >
                      <SectionChip section={group.section} />
                      <span className="font-bold text-primaryDark">{group.title}</span>
                    </div>
                    {group.match_reason ? (
                      <p className="mt-1 text-sm font-semibold text-primaryBlue">
                        {group.match_reason}
                      </p>
                    ) : group.match_quote ? (
                      // No structured reason: the backend sends the matching transcript
                      // line (centered on the search term) as the connection evidence.
                      <p className="mt-1 text-sm italic text-primaryDark opacity-75">
                        {`“${group.match_quote}”`}
                      </p>
                    ) : null}
                    {description ? (
                      <p className="mt-1 text-sm text-primaryDark">{description}</p>
                    ) : null}
                    {moments.length > 0 ? (
                      <ul className="mt-2 flex flex-wrap gap-2">
                        {moments.map((item, i) => (
                          <li key={`${group.class_id}-${item.start_ms}-${i}`}>
                            <button
                              type="button"
                              onClick={() => onMomentClick(group, item, groupRank)}
                              className="rounded bg-primaryBlue text-white text-xs px-2 py-1 hover:opacity-80"
                            >
                              {`▶ ${formatTimestamp(item.start_ms)}`}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
